import { useState } from 'react';
import './license-data-review.scss';

interface ExtractedData {
  plateColor: string;
  plateNumber: string;
  plateSource: string;
  plateCategory: string;
}

interface LicenseDataReviewProps {
  extractedData: ExtractedData;
  onConfirm: (data: ExtractedData) => void;
  language?: 'ar' | 'en' | 'ur';
}

export default function LicenseDataReview({ 
  extractedData, 
  onConfirm, 
  language = 'ar' 
}: LicenseDataReviewProps) {
  const [plateColor, setPlateColor] = useState(extractedData.plateColor || '');
  const [plateNumber, setPlateNumber] = useState(extractedData.plateNumber || '');
  const [plateSource, setPlateSource] = useState(extractedData.plateSource || '');
  const [plateCategory, setPlateCategory] = useState(extractedData.plateCategory || '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const texts = {
    ar: {
      title: 'مراجعة البيانات المستخرجة',
      subtitle: 'يرجى مراجعة البيانات وتعديلها إذا لزم الأمر',
      plateColor: 'لون/رمز اللوحة',
      plateColorPlaceholder: 'مثال: B أو WHITE',
      plateNumber: 'رقم اللوحة',
      plateNumberPlaceholder: 'مثال: 1328',
      plateSource: 'مكان الإصدار',
      plateSourcePlaceholder: 'مثال: دبي',
      plateCategory: 'صنف اللوحة',
      plateCategoryPlaceholder: 'مثال: خصوصي',
      confirm: 'تأكيد البيانات',
      requiredError: 'هذا الحقل مطلوب',
      note: 'ملاحظة: تأكد من صحة البيانات قبل التأكيد',
    },
    en: {
      title: 'Review Extracted Data',
      subtitle: 'Please review and edit the data if necessary',
      plateColor: 'Plate Color/Code',
      plateColorPlaceholder: 'Example: B or WHITE',
      plateNumber: 'Plate Number',
      plateNumberPlaceholder: 'Example: 1328',
      plateSource: 'Place of Issue',
      plateSourcePlaceholder: 'Example: Dubai',
      plateCategory: 'Plate Category',
      plateCategoryPlaceholder: 'Example: Private',
      confirm: 'Confirm Data',
      requiredError: 'This field is required',
      note: 'Note: Make sure the data is correct before confirming',
    },
    ur: {
      title: 'نکالے گئے ڈیٹا کا جائزہ',
      subtitle: 'براہ کرم ڈیٹا کا جائزہ لیں اور ضرورت ہو تو ترمیم کریں',
      plateColor: 'پلیٹ کا رنگ/کوڈ',
      plateColorPlaceholder: 'مثال: B یا WHITE',
      plateNumber: 'پلیٹ نمبر',
      plateNumberPlaceholder: 'مثال: 1328',
      plateSource: 'جاری کرنے کی جگہ',
      plateSourcePlaceholder: 'مثال: دبئی',
      plateCategory: 'پلیٹ کی قسم',
      plateCategoryPlaceholder: 'مثال: نجی',
      confirm: 'ڈیٹا کی تصدیق کریں',
      requiredError: 'یہ فیلڈ ضروری ہے',
      note: 'نوٹ: تصدیق سے پہلے ڈیٹا کی درستگی کو یقینی بنائیں',
    }
  };

  const t = texts[language];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!plateColor.trim()) {
      newErrors.plateColor = t.requiredError;
    }
    if (!plateNumber.trim()) {
      newErrors.plateNumber = t.requiredError;
    }
    if (!plateSource.trim()) {
      newErrors.plateSource = t.requiredError;
    }
    if (!plateCategory.trim()) {
      newErrors.plateCategory = t.requiredError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (validateForm()) {
      onConfirm({
        plateColor: plateColor.trim(),
        plateNumber: plateNumber.trim(),
        plateSource: plateSource.trim(),
        plateCategory: plateCategory.trim(),
      });
    }
  };

  return (
    <div className="license-data-review-container">
      <div className="review-header">
        <h3>{t.title}</h3>
        <p className="subtitle">{t.subtitle}</p>
      </div>

      <div className="form-container">
        <div className="form-field">
          <label htmlFor="plateColor">
            {t.plateColor}
            <span className="required">*</span>
          </label>
          <input
            id="plateColor"
            type="text"
            value={plateColor}
            onChange={(e) => {
              setPlateColor(e.target.value);
              if (errors.plateColor) {
                setErrors({ ...errors, plateColor: '' });
              }
            }}
            placeholder={t.plateColorPlaceholder}
            className={errors.plateColor ? 'error' : ''}
          />
          {errors.plateColor && (
            <span className="error-message">
              <span className="material-symbols-outlined">error</span>
              {errors.plateColor}
            </span>
          )}
        </div>

        <div className="form-field">
          <label htmlFor="plateNumber">
            {t.plateNumber}
            <span className="required">*</span>
          </label>
          <input
            id="plateNumber"
            type="text"
            value={plateNumber}
            onChange={(e) => {
              setPlateNumber(e.target.value);
              if (errors.plateNumber) {
                setErrors({ ...errors, plateNumber: '' });
              }
            }}
            placeholder={t.plateNumberPlaceholder}
            className={errors.plateNumber ? 'error' : ''}
          />
          {errors.plateNumber && (
            <span className="error-message">
              <span className="material-symbols-outlined">error</span>
              {errors.plateNumber}
            </span>
          )}
        </div>

        <div className="form-field">
          <label htmlFor="plateSource">
            {t.plateSource}
            <span className="required">*</span>
          </label>
          <input
            id="plateSource"
            type="text"
            value={plateSource}
            onChange={(e) => {
              setPlateSource(e.target.value);
              if (errors.plateSource) {
                setErrors({ ...errors, plateSource: '' });
              }
            }}
            placeholder={t.plateSourcePlaceholder}
            className={errors.plateSource ? 'error' : ''}
          />
          {errors.plateSource && (
            <span className="error-message">
              <span className="material-symbols-outlined">error</span>
              {errors.plateSource}
            </span>
          )}
        </div>

        <div className="form-field">
          <label htmlFor="plateCategory">
            {t.plateCategory}
            <span className="required">*</span>
          </label>
          <input
            id="plateCategory"
            type="text"
            value={plateCategory}
            onChange={(e) => {
              setPlateCategory(e.target.value);
              if (errors.plateCategory) {
                setErrors({ ...errors, plateCategory: '' });
              }
            }}
            placeholder={t.plateCategoryPlaceholder}
            className={errors.plateCategory ? 'error' : ''}
          />
          {errors.plateCategory && (
            <span className="error-message">
              <span className="material-symbols-outlined">error</span>
              {errors.plateCategory}
            </span>
          )}
        </div>
      </div>

      <div className="note-box">
        <span className="material-symbols-outlined">info</span>
        <span>{t.note}</span>
      </div>

      <button className="confirm-button" onClick={handleConfirm}>
        <span className="material-symbols-outlined">check_circle</span>
        {t.confirm}
      </button>
    </div>
  );
}

